import { SQLFilters } from '../../../../api/types';
import { RegularMetricId } from '../../../../api/types-graphql';
import {
  ChartGridConfig,
  KpiFactboxBenchmarkPeriods,
  KpiFactboxBenchmarkTypes,
  KpiV2ComponentConfig,
  KpiV2FontSize,
  KpiV2Type,
  LanguageString,
  LineChartComponentConfig,
  LineChartQueryConfig,
  TimePeriodBarChartComponentConfig,
  TimePeriodBarChartQueryConfig,
} from '../../../../common/components/dashboards/types';
import { Languages } from '../../../../constants';
import {
  DataFieldWithDataType,
  TimeSelection,
  TimeSelectionType,
} from '../../../../types';
import { getKeyFromDataFieldWithDataType } from '../../../../utils';
import { StoryboardDateUtils } from '../../utils';
import { getFactboxGridConfig, topLineChartGridConfig } from '../chart-layouts';

export const getHeadcountsTopChartConfig = (
  storyboardDateUtils: StoryboardDateUtils,
  filters: SQLFilters | null
): LineChartComponentConfig => {
  const { twelveMonthsAgoFromNowEndOfMonth, nowEndOfMonth } =
    storyboardDateUtils;
  const query: LineChartQueryConfig = {
    metrics: [
      {
        type: 'RegularMetricId',
        value: RegularMetricId.Pm0001_1IndividualsCount,
      },
    ],
    timeSelection: {
      type: TimeSelectionType.CalendarYearMonthly,
      input: {
        start: twelveMonthsAgoFromNowEndOfMonth,
        end: nowEndOfMonth,
      },
    },
    filters: filters ?? undefined,
  };
  return {
    chartId: 'top-chart',
    chartTypeConfig: {
      chartType: 'line',
    },
    title: {
      [Languages.EN]: 'Individuals Historical',
      [Languages.JA]: 'Individuals Historical',
    },
    queries: [query],
    ...topLineChartGridConfig,
  };
};

export const getHeadcountsByDimensionChartConfig = (
  storyboardDateUtils: StoryboardDateUtils,
  dimension: DataFieldWithDataType,
  title: LanguageString,
  filters: SQLFilters | null,
  gridConfig: ChartGridConfig
): TimePeriodBarChartComponentConfig => {
  const { nowEndOfMonth } = storyboardDateUtils;
  const query: TimePeriodBarChartQueryConfig = {
    metrics: [
      {
        type: 'RegularMetricId',
        value: RegularMetricId.Pm0001_1IndividualsCount,
      },
    ],
    timeSelection: {
      type: TimeSelectionType.CalendarYearSingleValueByMonths,
      input: {
        start: nowEndOfMonth,
        end: nowEndOfMonth,
      },
    },
    segmentations: [
      {
        type: 'non-hierarchical',
        dataField: dimension,
      },
    ],
    filters: filters ?? undefined,
  };
  return {
    chartId: `by-dimension-chart-${getKeyFromDataFieldWithDataType(dimension)}`,
    chartTypeConfig: {
      chartType: 'bar',
      timeframe: 'timePeriod',
    },
    title,
    queries: [query],
    ...gridConfig,
  };
};

export const getNikkiHeadcountFactbox = (
  storyboardDateUtils: StoryboardDateUtils,
  filters: SQLFilters | null
): KpiV2ComponentConfig => {
  const { nowEndOfMonth } = storyboardDateUtils;
  const timeSelection: TimeSelection = {
    type: TimeSelectionType.CalendarYearSingleValueByMonths,
    input: {
      start: nowEndOfMonth,
      end: nowEndOfMonth,
    },
  };
  const kpiConfig: KpiV2ComponentConfig = {
    chartId: 'headcount-factbox',
    chartTypeConfig: {
      chartType: 'kpi-v2',
    },
    title: {
      [Languages.EN]: 'Headcount',
      [Languages.JA]: 'Headcount',
    },
    options: {
      kpiType: KpiV2Type.SINGLE_METRIC,
      fontSize: KpiV2FontSize.SMALL,
      section: {
        key: 'headcount',
        query: {
          metrics: [
            {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0001_1IndividualsCount,
            },
          ],
          timeSelection,
          filters: filters ?? undefined,
        },
        benchmarkConfigs: [
          {
            benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
            benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
          },
        ],
      },
    },
    ...getFactboxGridConfig(1),
  };
  return kpiConfig;
};

export const getNikkiTenureFactbox = (
  storyboardDateUtils: StoryboardDateUtils,
  filters: SQLFilters | null
): KpiV2ComponentConfig => {
  const { nowEndOfMonth } = storyboardDateUtils;
  const timeSelection: TimeSelection = {
    type: TimeSelectionType.CalendarYearSingleValueByMonths,
    input: {
      start: nowEndOfMonth,
      end: nowEndOfMonth,
    },
  };
  const kpiConfig: KpiV2ComponentConfig = {
    chartId: 'tenure-factbox',
    chartTypeConfig: {
      chartType: 'kpi-v2',
    },
    title: {
      [Languages.EN]: 'Average Tenure',
      [Languages.JA]: 'Average Tenure',
    },
    options: {
      kpiType: KpiV2Type.SINGLE_METRIC,
      fontSize: KpiV2FontSize.SMALL,
      section: {
        key: 'tenure',
        query: {
          metrics: [
            {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0023_2TenureAvg,
            },
          ],
          timeSelection,
          filters: filters ?? undefined,
        },
        benchmarkConfigs: [
          {
            benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
            benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
          },
        ],
      },
    },
    ...getFactboxGridConfig(2),
  };
  return kpiConfig;
};

export const getNikkiGenderRatioFactbox = (
  storyboardDateUtils: StoryboardDateUtils,
  filters: SQLFilters | null
): KpiV2ComponentConfig => {
  const { nowEndOfMonth } = storyboardDateUtils;
  const timeSelection: TimeSelection = {
    type: TimeSelectionType.CalendarYearSingleValueByMonths,
    input: {
      start: nowEndOfMonth,
      end: nowEndOfMonth,
    },
  };
  const kpiConfig: KpiV2ComponentConfig = {
    chartId: 'gender-ratio-factbox',
    chartTypeConfig: {
      chartType: 'kpi-v2',
    },
    title: {
      [Languages.EN]: 'Gender Ratio',
      [Languages.JA]: 'Gender Ratio',
    },
    options: {
      kpiType: KpiV2Type.DOUBLE_METRIC,
      fontSize: KpiV2FontSize.SMALL,
      sections: {
        leftSection: {
          key: 'female',
          title: {
            [Languages.EN]: 'Female',
            [Languages.JA]: 'Female',
          },
          query: {
            metrics: [
              {
                type: 'RegularMetricId',
                value: RegularMetricId.Pm0020_2FhcPercHc,
              },
            ],
            timeSelection,
            filters: filters ?? undefined,
          },
          benchmarkConfigs: [
            {
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
            },
          ],
        },
        rightSection: {
          key: 'male',
          title: {
            [Languages.EN]: 'Male',
            [Languages.JA]: 'Male',
          },
          query: {
            metrics: [
              {
                type: 'RegularMetricId',
                value: RegularMetricId.Pm0448_2MhcPerPerson,
              },
            ],
            timeSelection,
            filters: filters ?? undefined,
          },
          benchmarkConfigs: [
            {
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
            },
          ],
        },
      },
    },
    ...getFactboxGridConfig(3),
  };
  return kpiConfig;
};

export const getNikkiAgeFactbox = (
  storyboardDateUtils: StoryboardDateUtils,
  filters: SQLFilters | null
): KpiV2ComponentConfig => {
  const { nowEndOfMonth } = storyboardDateUtils;
  const timeSelection: TimeSelection = {
    type: TimeSelectionType.CalendarYearSingleValueByMonths,
    input: {
      start: nowEndOfMonth,
      end: nowEndOfMonth,
    },
  };
  const kpiConfig: KpiV2ComponentConfig = {
    chartId: 'headcount-factbox',
    chartTypeConfig: {
      chartType: 'kpi-v2',
    },
    title: {
      [Languages.EN]: 'Average Age',
      [Languages.JA]: 'Average Age',
    },
    options: {
      kpiType: KpiV2Type.SINGLE_METRIC,
      fontSize: KpiV2FontSize.SMALL,
      section: {
        key: 'age',
        query: {
          metrics: [
            { type: 'RegularMetricId', value: RegularMetricId.Pm0019_2AgeAvg },
          ],
          timeSelection,
          filters: filters ?? undefined,
        },
        benchmarkConfigs: [
          {
            benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
            benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
          },
        ],
      },
    },
    ...getFactboxGridConfig(4),
  };
  return kpiConfig;
};
